import React, { useEffect, useState } from 'react';
import ReactSlider from 'react-slider';
import IconHamburger from '../../../ui/icons/IconHamburger';
import { convertNumberToString, convertStringToNumber, roundToNearest } from '../../helpers/formatting';

const INITIAL_SLIDER_VALUE = [ 0, 100 ];

const convertDisplayToNumber = ( textDisplay ) => {
	return convertStringToNumber( textDisplay );
};

const convertNumberToSliderValue = ( { num, minRange, totalRange, roundTo } ) => {
	return ( ( num - minRange ) / totalRange ) * 100;
};

const convertSliderValueToNumber = ( { sliderValue, minRange, totalRange, roundTo } ) => {
	const numberToRound = Math.round( ( sliderValue / 100 ) * totalRange ) + minRange;
	return roundToNearest( numberToRound, roundTo );
};

/*
    Filter for attributes that have a max and min, e.g. price and sq ft
    Includes slider and 2 inputs for min and max
        - Internally, min and max are stored as numbers, but displayed as formatted strings

    props:
     	range - array of numbers e.g. [ 1000, 5000 ]
 	        - originally this was a dynamic range based on available values in index, but moved to a static range
 	        - if range is dynamic - needs to not be 0,0 on first render - which is taken care of by parent component SearchFilterSqFtData
		name - string, e.g. Sq ft - used in aria labels and as input name
        roundTo - defaults to 100 - number to round values to, e.g. 100 or 1000
        onChange - function to call when value changes to update filters string
        format - currency | sqft
        label - string, e.g. Square Footage - display
		valuesToResetOn - array of 2 numbers and null values to track to know when to reset slider, when both are null, slider is reset
        attributeMin - min_sq_feet - used for analytics
        attributeMax - max_sq_feet - used for analytics
		initialValues - array of 2 numbers, e.g. [ 1000, 5000 ] - used to set initial values from url
 */
// total range 800,000
const SearchFiltersRange = ( props ) => {
	const { range, name, roundTo = 100, onChange, format, label, valuesToResetOn = [], attributeMin, attributeMax, initialValues } = props;

	// range does not change
	const rangeMin = range[ 0 ];
	const rangeMax = range[ 1 ];
	const totalRange = rangeMax - rangeMin;
	// percentage array - e.g. [0, 100] default or initial value from url
	const [ sliderValue, setSliderValue ] = useState( null );
	// values for inputs, full value as strings
	const [ currentMinAsString, setCurrentMinAsString ] = useState( null );
	const [ currentMaxAsString, setCurrentMaxAsString ] = useState( null );

	useEffect( () => {
		// initial value could be default range or min/max from url
		const minAsNumber = initialValues[ 0 ] || range[ 0 ];
		const newMinSlider = convertNumberToSliderValue( { num: minAsNumber, minRange: range[ 0 ], totalRange, roundTo } );

		const maxAsNumber = initialValues[ 1 ] || range[ 1 ];
		const newMaxSlider = convertNumberToSliderValue( { num: maxAsNumber, minRange: range[ 0 ], totalRange, roundTo } );

		const newRange = [ newMinSlider, newMaxSlider ];
		// slider uses 0-100, but value needs to be converted to sqft by percentage
		setSliderValue( newRange );
		setCurrentMinAsString( convertNumberToDisplay( minAsNumber ) );
		setCurrentMaxAsString( convertNumberToDisplay( maxAsNumber ) );
	}, [] );

	//
	const convertNumberToDisplay = ( num ) => {
		const textDisplay = convertNumberToString( num );
		return num === range[ 1 ] ? `${ textDisplay }+` : textDisplay;
	};

	// reset slider when filters are cleared elsewhere
	useEffect( () => {
		if ( valuesToResetOn.length === 0 ) {
			return;
		}
		// only reset slider if both values are null so it doesn't reset when slider is moved to min or max
		if ( valuesToResetOn.every( ( value ) => value === null ) && sliderValue !== INITIAL_SLIDER_VALUE ) {
			setSliderValue( INITIAL_SLIDER_VALUE );
			setCurrentMinAsString( convertNumberToDisplay( range[ 0 ] ) );
			setCurrentMaxAsString( convertNumberToDisplay( range[ 1 ] ) );
			// console.log( 'slider reset' );
		}
	}, [ valuesToResetOn, sliderValue, range ] );

	if ( ! sliderValue ) {
		return null;
	}

	return (
		<div>
			<div className='tw-font-bold tw-mb-6'>{label}</div>
			<div className="tw-block tw-h-8">
				<ReactSlider
					className="gtm-search-filter__range tw-w-full"
					thumbClassName="-tw-mt-[11px] tw-shadow tw-h-6 tw-w-6 tw-rounded-full tw-bg-white tw-border tw-border-charcoal tw-outline-none tw-shadow-md tw-cursor-pointer tw-transition tw-ease-in-out tw-duration-150"
					trackClassName="slider-filter--track"
					step={1}
					pearling
					onChange={( newValue, index ) => {
						const newMin = convertSliderValueToNumber( { sliderValue: newValue[ 0 ], totalRange, minRange: range[ 0 ], roundTo } );
						const newMax = convertSliderValueToNumber( { sliderValue: newValue[ 1 ], totalRange, minRange: range[ 0 ], roundTo } );
						setCurrentMinAsString( convertNumberToDisplay( newMin ) );
						setCurrentMaxAsString( convertNumberToDisplay( newMax ) );
						setSliderValue( newValue );
						onChange( newMin, newMax );
					}}
					defaultValue={INITIAL_SLIDER_VALUE}
					value={sliderValue}
					minDistance={10}
					ariaLabel={[ `${ name } min`, `${ name } max` ]}
					ariaValuetext={( state ) => `${ name } min: ${ sliderValue[ 0 ] } max: ${ sliderValue[ 1 ] }`}
					renderThumb={( propsThumb, state ) => <div {...propsThumb}><div className="tw-h-full tw-flex tw-justify-center tw-items-center"><IconHamburger /></div></div>}
				/>
			</div>
			<div className="tw-grid tw-grid-cols-2 tw-gap-6 tw-space-x-1 tw-justify-between tw-items-center">
				<div className="tw-relative after:tw-content-['—'] after:tw-absolute after:-tw-right-6 after:tw-top-4 after:tw-bottom-0 after:tw-margin-auto">
					<label className="tw-absolute tw-top-2 tw-left-2 tw-uppercase tw-text-xxxxs tw-font-bold tw-text-slate" htmlFor={`${ attributeMin }`}>Minimum</label>
					{format === 'currency' && (
						<span className="tw-absolute tw-bottom-[10px] tw-left-2 tw-text-base">$</span>
					)}
					{format === 'sqft' && (
						<span className="tw-absolute tw-bottom-3 tw-right-3 tw-text-xxxxs">sq. ft.</span>
					)}
					<input
						className={`${ format === 'currency' ? 'tw-pl-4 tw-pr-2' : 'tw-px-2' } tw-pt-6 tw-pb-2 tw-border-steel tw-border tw-w-full`}
						data-insights-filters={`${ attributeMin } < ${ convertDisplayToNumber( currentMinAsString ) }`}
						type="text"
						name={`${ attributeMin }`}
						value={currentMinAsString}
						onFocus={( event ) => event.target.select()}
						onChange={( e ) => {
							let newMinAsNumber = convertDisplayToNumber( e.target.value );
							// handle ''
							if ( ! newMinAsNumber ) {
								setCurrentMinAsString( '' );
								return;
							}
							// value is greater than max, don't update slider ui, just this input
							if ( newMinAsNumber > convertDisplayToNumber( currentMaxAsString ) ) {
								setCurrentMinAsString( convertNumberToDisplay( newMinAsNumber ) );
								return;
							}
							// value is smaller than min, don't update slider ui, just this input
							if ( newMinAsNumber < range[ 0 ] ) {
								setCurrentMinAsString( convertNumberToDisplay( newMinAsNumber ) );
								return;
							}
							// if new min is under initial min, use initial max
							newMinAsNumber = newMinAsNumber < range[ 0 ] ? range[ 0 ] : newMinAsNumber;
							const newMinAsString = convertNumberToDisplay( newMinAsNumber );
							const newMinSlider = ( ( newMinAsNumber - range[ 0 ] ) / totalRange ) * 100;
							const newRange = [ Math.round( newMinSlider ), sliderValue[ 1 ] ];
							setSliderValue( newRange );
							setCurrentMinAsString( newMinAsString );
							onChange( newMinAsNumber, convertDisplayToNumber( currentMaxAsString ) );
						}}
					/>
				</div>
				<div className="tw-relative">
					<label className="tw-absolute tw-top-2 tw-left-2 tw-uppercase tw-text-xxxxs tw-font-bold tw-text-slate" htmlFor={`${ attributeMax }`}>Maximum</label>
					{format === 'currency' && (
						<span className="tw-absolute tw-bottom-[10px] tw-left-2 tw-text-base">$</span>
					)}
					{format === 'sqft' && (
						<span className="tw-absolute tw-bottom-3 tw-right-3 tw-text-xxxxs">sq. ft.</span>
					)}
					<input
						className={`${ format === 'currency' ? 'tw-pl-4 tw-pr-2' : 'tw-px-2' } tw-pt-6 tw-pb-2 tw-border-steel tw-border tw-w-full`}
						data-insights-filters={`${ attributeMax } < ${ convertDisplayToNumber( currentMaxAsString ) }`}
						type="text"
						name={`${ attributeMax }`}
						value={currentMaxAsString}
						onFocus={( event ) => event.target.select()}
						onChange={( e ) => {
							let newMaxAsNumber = convertDisplayToNumber( e.target.value );
							// handle ''
							if ( ! newMaxAsNumber ) {
								setCurrentMaxAsString( '' );
								return;
							}
							// handle min over max
							if ( newMaxAsNumber < convertDisplayToNumber( currentMinAsString ) ) {
								setCurrentMaxAsString( convertNumberToDisplay( newMaxAsNumber ) );
								return;
							}

							// if new max is over max, use initial max as new
							newMaxAsNumber = newMaxAsNumber > range[ 1 ] ? range[ 1 ] : newMaxAsNumber;
							const newMaxSlider = ( ( newMaxAsNumber - range[ 0 ] ) / totalRange ) * 100;
							setSliderValue( [ sliderValue[ 0 ], Math.round( newMaxSlider ) ] );
							onChange( convertDisplayToNumber( currentMinAsString ), newMaxAsNumber );
							setCurrentMaxAsString( convertNumberToDisplay( newMaxAsNumber ) );
						}}
					/>
				</div>
			</div>
		</div>
	);
};

export default SearchFiltersRange;
